import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "../components/InvoicePrint.css";

const InvoicePrint = () => {
  const { invoice_id, patient_id } = useParams();

  const [invoice, setInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoice, setLoadingInvoice] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  const invoiceRef = useRef(null);
  const [hideButtons, setHideButtons] = useState(false);

  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [displayInvoiceDate, setDisplayInvoiceDate] = useState("");

  // ================= FETCH INVOICE =================
  useEffect(() => {
    if (invoice_id) fetchInvoicePrintData();
  }, [invoice_id]);

  const fetchInvoicePrintData = async () => {
    try {
      const res = await fetch(
        `https://newerp.awasthivitality.com/api/invoices/${invoice_id}/print`
      );
      const json = await res.json();

      if (json.success && json.data) {
        setInvoice(json.data);
        setDisplayInvoiceDate(
          json.data.invoice_date ||
            new Date().toISOString().split("T")[0]
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingInvoice(false);
    }
  };

  // ================= FETCH PATIENT INVOICES =================
  useEffect(() => {
    if (!patient_id) return;

    axios
      .get(
        `https://newerp.awasthivitality.com/api/invoice/list/${patient_id}`
      )
      .then((res) => {
        if (res.data.success) setInvoices(res.data.data || []);
      })
      .catch(console.error)
      .finally(() => setLoadingInvoices(false));
  }, [patient_id]);

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = async (finalDate) => {
    setHideButtons(true);

    const element = invoiceRef.current;

    // PDF MODE ON
    element.classList.add("invoice-pdf-capture");

    await new Promise((r) => setTimeout(r, 100));

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      windowWidth: element.scrollWidth,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const imgWidth = 190;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // pdf.text(`Invoice Date: ${finalDate}`, 10, 15);
    pdf.addImage(imgData, "PNG", 10, 25, imgWidth, imgHeight);
    pdf.save(`invoice_${invoice_id}.pdf`);

    // CLEANUP
    element.classList.remove("invoice-pdf-capture");
    setHideButtons(false);

    // FORCE REFLOW (FIX LAYOUT BUG)
    void element.offsetHeight;
  };

  if (loadingInvoice) return <div>Loading invoice...</div>;
  if (!invoice) return <div>Invoice not found</div>;

  const items = invoice.items || [];
  const patient = invoice.patient || {};
  const summary = invoice.summary || {};

  return (
    <div className="invoice-print-wrapper" ref={invoiceRef}>
      {/* ⭐ LETTERHEAD SPACE (PDF ONLY) */}
      <div className="pdf-letterhead-space"></div>

      <div className="invoice-header">
        <div><strong>Invoice No:</strong> {invoice.invoice_no}</div>
        <div><strong>Invoice Date:</strong> {displayInvoiceDate}</div>
        <div><strong>PR No:</strong> {patient.pr_no}</div>
        <div><strong>Patient:</strong> {patient.name}</div>
        <div><strong>Mobile:</strong> {patient.mobile}</div>
        <div><strong>City:</strong> {patient.city}</div>
        <div><strong>Address:</strong> {patient.address}</div>
      </div>

      <table className="invoice-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Discount</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{it.name}</td>
              <td>{it.quantity}</td>
              <td>{it.unit_price}</td>
              <td>{it.discount}</td>
              <td>₹{(it.quantity * it.unit_price) - it.discount}</td>
            </tr>
          ))}
          <tr>
            <td colSpan="5" style={{ textAlign: "right" }}>
              <strong>Grand Total</strong>
            </td>
            <td><strong>₹{summary.grand_total}</strong></td>
          </tr>
        </tbody>
      </table>

      <div className="invoice-footer">
        <p>1. No exchange or return</p>
        <p>2. Subject to RAIPUR jurisdiction</p>
        <div className="signature">Signature</div>
      </div>

      {!hideButtons && (
        <div className="print-btn-container">
          <button onClick={() => setShowDateModal(true)}>
            Download PDF
          </button>
        </div>
      )}

      {/* DATE MODAL */}
      {showDateModal && (
        <div className="print-modal-overlay">
          <div className="print-modal">
            <h3>🖨️ Download Invoice</h3>
            <p>
              Select invoice date
              <br />
              <small>(Leave empty for today)</small>
            </p>

            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowDateModal(false);
                  setSelectedDate("");
                }}
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  const finalDate =
                    selectedDate ||
                    new Date().toISOString().split("T")[0];

                  setDisplayInvoiceDate(finalDate);
                  setShowDateModal(false);
                  setSelectedDate("");
                  handleDownloadPDF(finalDate);
                }}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePrint;
